import React, { useState } from 'react';
import { signInWithEmailAndPassword, linkWithCredential, getAdditionalUserInfo } from 'firebase/auth';
import type { OAuthCredential, User } from 'firebase/auth';
import { auth } from '../firebase';
import type { LastProvider } from '../storage/savedProfile';

type HandleUserOpts = {
  remember?: boolean;
  provider?: LastProvider;
  profile?: { displayName?: string | null; photoURL?: string | null };
};

interface Props {
  email: string;
  credential: OAuthCredential;
  onUser: (user: User, opts?: HandleUserOpts) => Promise<void>;
  onError: (message: string) => void;
  onSignInFailure: (message: string) => void;
  onBack: () => void;
}

function Spinner() {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-sf-blue-dark border-t-transparent" />
  );
}

export function LinkAccountPanel({ email, credential, onUser, onError, onSignInFailure, onBack }: Props) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const linked = await linkWithCredential(result.user, credential);
      const info = getAdditionalUserInfo(linked);
      const p = info?.profile as { name?: string; picture?: string } | undefined;
      await onUser(linked.user, {
        remember: true,
        provider: 'google',
        profile: { displayName: p?.name ?? null, photoURL: p?.picture ?? null },
      });
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        onSignInFailure('Incorrect password.');
      } else if (code === 'auth/too-many-requests') {
        onSignInFailure('Too many attempts. Try again later.');
      } else if (code === 'auth/credential-already-in-use') {
        onError('This Google account is already linked to a different user.');
      } else {
        onError(err instanceof Error ? err.message : 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleLink} className="flex flex-col gap-5">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sf-blue-vibrant/20">
          <svg className="h-6 w-6 text-sf-blue-vibrant" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
          </svg>
        </div>
        <h2 className="font-khand-semibold text-2xl text-gray-800 dark:text-white">One last step</h2>
      </div>

      {/* Info box */}
      <div className="rounded-xl bg-sf-blue-vibrant/10 px-4 py-3 text-sm">
        <p className="text-gray-500 dark:text-gray-400">Signing in as</p>
        <p className="mt-0.5 font-semibold break-all text-sf-blue-dark dark:text-white">{email}</p>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          Enter your password to continue. Google will be added as a sign-in option for future logins.
        </p>
      </div>

      {/* Password */}
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoFocus
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-11 text-sm text-sf-blue-dark placeholder-gray-400 outline-none focus:border-sf-blue-vibrant dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-gray-500"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 hover:text-sf-blue-dark dark:text-gray-500 dark:hover:text-white"
        >
          {showPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </button>
      </div>

      <button
        type="submit"
        disabled={loading || password.length === 0}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-sf-blue-vibrant py-3 font-medium text-white transition hover:bg-sf-blue-vibrant-dark disabled:opacity-60"
      >
        {loading && <Spinner />}
        {loading ? 'Signing in…' : 'Sign in'}
      </button>

      <button
        type="button"
        onClick={onBack}
        disabled={loading}
        className="text-center text-sm text-gray-500 transition hover:text-gray-300 disabled:opacity-40"
      >
        ← Use email & password instead
      </button>
    </form>
  );
}
